import React, { useState } from 'react';
import { FileEdit, Eye, Sparkles, X, RefreshCw } from 'lucide-react';
import ContentEditor from './components/ContentEditor';
import PublishingHub from './components/PublishingHub';
import {
  DEFAULT_CATEGORIES,
  DEFAULT_POST,
  publishPost,
  saveDraft,
  generateAiContent,
} from './blogService';

export default function BlogEditorPage() {
  // Post state (shared between ContentEditor and PublishingHub)
  const [postTitle, setPostTitle] = useState(DEFAULT_POST.title);
  const [contentHtml, setContentHtml] = useState(DEFAULT_POST.html);
  const [contentType, setContentType] = useState(DEFAULT_POST.contentType);
  const [publishStatus, setPublishStatus] = useState(DEFAULT_POST.status);
  const [selectedCategories, setSelectedCategories] = useState(DEFAULT_POST.categories);
  const [availableCategories, setAvailableCategories] = useState(DEFAULT_CATEGORIES);
  const [featuredImage, setFeaturedImage] = useState(DEFAULT_POST.featuredImage);

  // Page UI state
  const [activeTab, setActiveTab] = useState('write'); // 'write' | 'preview'
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);

  // AI modal state
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);

  const buildPost = () => ({
    title: postTitle,
    html: contentHtml.replace(/\u200B/g, ''),
    contentType,
    status: publishStatus,
    categories: selectedCategories,
    featuredImage,
  });

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      await publishPost(buildPost());
      setPublishSuccess(true);
      setTimeout(() => setPublishSuccess(false), 4000);
    } catch (err) {
      console.error(err);
      alert('Publishing failed. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSaveDraft = async () => {
    await saveDraft(buildPost());
    alert('Draft saved locally in CRM context!');
  };


  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 h-full">
      {/* TOP BAR */}
      <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md px-6 flex items-center justify-between z-10">
        <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <span>Blogs & Newsletter Editor</span>
        </h1>

        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
            {[
              { id: 'write', label: 'Write', Icon: FileEdit },
              { id: 'preview', label: 'Live Preview', Icon: Eye },
            ].map(({ id, label, Icon }) => (
                <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeTab === id
                    ? 'bg-sky-500 text-white shadow-sm'
                    : 'bg-white text-black hover:bg-slate-50'
                }`}
                >
                <Icon className="w-3.5 h-3.5" />
                {label}
                </button>
            ))}
          </div>
        </div>
      </header>

      {/* WORKSPACE: editor + publishing hub */}
      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 overflow-y-auto p-6 lg:p-10 flex justify-center custom-scrollbar">
          <ContentEditor
            activeTab={activeTab}
            postTitle={postTitle}
            setPostTitle={setPostTitle}
            contentHtml={contentHtml}
            setContentHtml={setContentHtml}
            contentType={contentType}
            featuredImage={featuredImage}
          />
        </main>

        <PublishingHub
          contentType={contentType}
          setContentType={setContentType}
          featuredImage={featuredImage}
          setFeaturedImage={setFeaturedImage}
          availableCategories={availableCategories}
          setAvailableCategories={setAvailableCategories}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          publishStatus={publishStatus}
          setPublishStatus={setPublishStatus}
          isPublishing={isPublishing}
          publishSuccess={publishSuccess}
          onPublish={handlePublish}
          onSaveDraft={handleSaveDraft}
        />
      </div>
    </div>
  );
}